import React, { useState, useEffect } from 'react'
import './CalendarTimeSettings.css'

/**
 * Component for configuring the visible time range in the weekly calendar.
 * Allows users to set start and end hours to reduce visual clutter.
 */
function CalendarTimeSettings({
  startHour,
  endHour,
  onSave,
  onReset,
  isDefault,
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [localStartHour, setLocalStartHour] = useState(startHour)
  const [localEndHour, setLocalEndHour] = useState(endHour)

  // Update local state when props change
  useEffect(() => {
    setLocalStartHour(startHour)
    setLocalEndHour(endHour)
  }, [startHour, endHour])

  const handleSave = () => {
    if (localStartHour >= localEndHour) {
      alert('Start time must be before end time')
      return
    }
    if (onSave(localStartHour, localEndHour)) {
      setIsExpanded(false)
    }
  }

  const handleReset = () => {
    if (onReset()) {
      setIsExpanded(false)
    }
  }

  const formatHour = (hour) => {
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
    return `${displayHour}:00 ${ampm}`
  }

  return (
    <div className="calendar-time-settings">
      <button
        className="btn btn-sm btn-outline-secondary settings-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        title="Configure visible calendar hours"
      >
        <span className="settings-icon">⚙️</span>
        <span className="settings-label">
          Time Range: {formatHour(startHour)} - {formatHour(endHour)}
        </span>
      </button>

      {isExpanded && (
        <>
          <div
            className="settings-backdrop"
            onClick={() => setIsExpanded(false)}
            aria-hidden="true"
          />
          <div className="settings-panel">
            <div className="settings-header">
              <h5>Visible Hours</h5>
              <button
                className="close-btn"
                onClick={() => setIsExpanded(false)}
                title="Close settings"
                aria-label="Close settings"
              >
                ×
              </button>
            </div>

            <div className="settings-body">
              <p className="settings-description">
                Customize which hours appear on the calendar to reduce clutter.
              </p>

              <div className="time-inputs">
                <div className="time-input-group">
                  <label htmlFor="start-hour">Start Hour</label>
                  <select
                    id="start-hour"
                    className="form-select form-select-sm"
                    value={localStartHour}
                    onChange={(e) =>
                      setLocalStartHour(parseInt(e.target.value))
                    }
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {formatHour(i)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="time-separator">to</div>

                <div className="time-input-group">
                  <label htmlFor="end-hour">End Hour</label>
                  <select
                    id="end-hour"
                    className="form-select form-select-sm"
                    value={localEndHour}
                    onChange={(e) => setLocalEndHour(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i} disabled={i <= localStartHour}>
                        {formatHour(i)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {localStartHour >= localEndHour && (
                <div className="alert alert-warning mt-2 mb-0">
                  Start time must be before end time
                </div>
              )}

              <div className="settings-actions">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleSave}
                  disabled={localStartHour >= localEndHour}
                >
                  Apply
                </button>
                {!isDefault && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={handleReset}
                  >
                    Reset to Default
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setIsExpanded(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default CalendarTimeSettings
